/**
 * src/components/Financial/CandleChartAnalysis.jsx
 * 【パフォーマンス最適化版】
 *
 * 修正内容：
 * 1. aspectSettingsのデフォルト値をuseMemoでメモ化
 * 2. useAstroDataに渡すパラメータを安定化
 * 3. stats依存を個別プロパティに分解
 * 4. 不要な再計算を防止
 */

import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
} from "react";
import {
  Sun,
  Moon,
  Check,
  Globe,
  Zap,
  ArrowLeftRight,
  Star,
  Target,
  Maximize,
  Minimize,
  Camera,
  MousePointer2,
  RefreshCw,
  Compass,
} from "lucide-react";
import {
  PLANET_SYMBOLS,
  PLANETS,
  PLANET_COLORS,
} from "../../utils/constants.js";
import { useAstroData } from "./useAstroData";

// モジュール群のインポート
import {
  calculateApogeePerigee,
  ApogeePerigeeSettingsUI,
  ApogeePerigeeLayer,
} from "./modules/ApogeePerigeeModule.jsx";
import {
  calculateAspectReturns,
  AspectReturnsSettingsUI,
  AspectReturnLayer,
  BaseDateAspectList,
} from "./modules/AspectReturnsModule.jsx";
import {
  calculateAngleReturns,
  AngleReturnSettingsUI,
  PlanetAngleReturnLayer,
  getPlanetPositions, // ★追加：これで ReferenceError が解消されます
  BaseDatePlanetList, // ★追加：これで ReferenceError が解消されます
} from "./modules/PlanetAngleReturnModule.jsx";
import {
  GannSquareLayer,
  GannSquareSettingsUI,
} from "./modules/GannSquareModule.jsx";
import {
  GannPlanetaryPriceLayer,
  GannPlanetaryPriceSettingsUI,
} from "./modules/GannPlanetaryPriceModule.jsx";
import {
  calculateMoonEvents,
  MoonPhaseLayer,
  MoonPhaseSettingsUI,
} from "./modules/MoonPhaseModule.jsx";

import {
  RetrogradeLayer,
  RetrogradeSettingsUI,
} from "./modules/RetrogradeModule.jsx";
import { IngressLayer, IngressSettingsUI } from "./modules/IngressModule.jsx";
import {
  calculateHelionTransits,
  HelionTransitLayer,
  HelionTransitSettingsUI,
} from "./modules/HelionTransitModule.jsx";
import {
  calculateLatitudeTransits,
  LatitudeTransitLayer,
  LatitudeTransitSettingsUI,
} from "./modules/LatitudeTransitModule.jsx";
import { GannFanLayer, GannFanSettingsUI } from "./modules/GannFanModule.jsx";
import {
  calculateComplexAspects,
  ComplexAspectLayer,
  ComplexAspectSettingsUI,
} from "./modules/ComplexAspectModule.jsx";
import {
  GannSquareLineLayer,
  GannSquareLineSettingsUI,
} from "./modules/GannSquareLineModule.jsx";
// ★ デフォルトのaspectSettingsをコンポーネント外に定義（参照が変わらない）
const DEFAULT_ASPECT_SETTINGS = {
  majorOrb: 8.0,
  minorOrb: 2.0,
  outerPlanetOrb: 1.0,
  useOuterLimit: false,
};
import {
  PyrapointLayer,
  PyrapointSettingsUI,
} from "./modules/PyrapointModule.jsx";

import {
  PlanetaryTrendlineLayer,
  PlanetaryTrendlineSettingsUI,
  calculatePlanetaryTrendlines,
} from "./modules/PlanetaryTrendlineModule.jsx";
import {
  calculatePlanetaryAspects,
  PlanetaryAspectLayer,
  PlanetaryAspectIconsLayer,
  PlanetaryAspectSettingsUI,
} from "./modules/PlanetaryAspectModule.jsx";
/**
 * 修正箇所：コンポーネント冒頭〜データ計算直前までの統合ブロック
 */
export default function CandleChartAnalysis(props) {
  var csvData = props.csvData || [];
  var observerLat = props.observerLat;
  var observerLon = props.observerLon;
  var natalPositions = props.natalPositions;

  // --- Refs ---
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const dragStart = useRef({
    x: 0,
    y: 0,
    startIndex: 0,
    priceCenterOffset: 0,
    multiplier: 1,
    count: 160,
  });
  const hasJumped = useRef(false);

  const pad = { l: 60, r: 80, t: 80, b: 100 }; // ★ b を 40 から 100 に増やしてスペースを確保
  var planetColors = props.planetColors || {};

  const getStored = (key, def) => {
    const val = localStorage.getItem(key);
    if (val === null) return def;
    try {
      return JSON.parse(val); // ここで文字列 "false" が boolean の false に変換される必要があります
    } catch (e) {
      // 文字列の "true" / "false" が入ってしまった場合のフォールバック
      if (val === "true") return true;
      if (val === "false") return false;
      return val;
    }
  };

  // --- 1. すべてのステート定義 (重複を排除し、不足分を補完) ---
  const [actualWidth, setActualWidth] = useState(1000);
  const [actualHeight, setActualHeight] = useState(600);
  const [timeframe, setTimeframe] = useState(() =>
    getStored("ast_timeframe", "D"),
  );

  const isDarkMode = useMemo(
    function () {
      const p = props.isDarkMode;

      // 1. プロップス判定 (propsが明示的に渡されている場合は最優先)
      if (p !== undefined && p !== null) {
        return p === true || p === "true" || p === "dark";
      }

      // 2. アプリ固有のLocalStorage判定 (App.jsxの管理キーに合わせる)
      if (typeof localStorage !== "undefined") {
        const saved = localStorage.getItem("astronica_theme");
        if (saved) return saved === "dark";
      }

      // 3. DOM判定 (html/bodyのクラスやOS設定)
      if (typeof document !== "undefined") {
        const html = document.documentElement;
        const body = document.body;
        const isSystemDark =
          window.matchMedia &&
          window.matchMedia("(prefers-color-scheme: dark)").matches;

        return (
          html.classList.contains("dark") ||
          body.classList.contains("dark") ||
          html.getAttribute("data-theme") === "dark" ||
          isSystemDark
        );
      }

      return true; // デフォルトはダーク
    },
    [props.isDarkMode],
  );

  // モードに合わせた色定数を定義
  const themeColors = useMemo(
    function () {
      const colors = isDarkMode
        ? {
          mainBg: "#0b0e14", // 深い黒
          subBg: "#1c1f26", // 少し明るい黒
          text: "#e5e7eb",
          border: "#2d313d",
          grid: "#1e222d",
        }
        : {
          mainBg: "#ffffff", // 純白
          subBg: "#f8fafc",
          text: "#1e293b",
          border: "#e2e8f0",
          grid: "#f1f5f9",
        };

      console.log("DEBUG: themeColors determined -> mainBg =", colors.mainBg);
      return colors;
    },
    [isDarkMode],
  );

  // --- 1. 表示ON/OFFフラグ群 (重複を排除し、すべてここに集約) ---
  const [isHeliocentric, setIsHeliocentric] = useState(() =>
    getStored("ast_isHeliocentric", false),
  );
  const [isSidereal, setIsSidereal] = useState(() =>
    getStored("ast_isSidereal", false),
  );
  const [visibleCount, setVisibleCount] = useState(() =>
    getStored("ast_visibleCount", 160),
  );
  const [startIndex, setStartIndex] = useState(0);
  const [priceRangeMultiplier, setPriceRangeMultiplier] = useState(1.0);
  const [priceCenterOffset, setPriceCenterOffset] = useState(0);

  const [showGannSquare, setShowGannSquare] = useState(() =>
    getStored("ast_showGannSquare", false),
  );
  const [showGannSquareLine, setShowGannSquareLine] = useState(() =>
    getStored("ast_showGannSquareLine", false),
  );
  const [showPlanetaryPriceLines, setShowPlanetaryPriceLines] = useState(() =>
    getStored("ast_showPlanetaryPriceLines", false),
  );
  const [showGannPlanetaryPrice, setShowGannPlanetaryPrice] = useState(() =>
    getStored("ast_showGannPlanetaryPrice", false),
  );
  // 先ほど追加した base と mirror のステートもここにあることを確認してください
  const [gannPlanetaryPriceScale, setGannPlanetaryPriceScale] = useState(() =>
    getStored("ast_gannPlanetaryPriceScale", 100),
  );

  // 垂直方向のオフセット
  const [gannPlanetaryPriceBase, setGannPlanetaryPriceBase] = useState(0);
  // 反転設定
  const [gannPlanetaryPriceMirror, setGannPlanetaryPriceMirror] =
    useState(false);

  const [gannSquare144Config, setGannSquare144Config] = useState(() =>
    getStored("ast_gannSquare144Cfg", {
      scale: 10,
      direction: "up",
      squareWidth: 144,
      showBox: true,
      showCross: true,
      showDiagonals: true,
      showFans12: true,
      showFans13: false,
      showFans14: false,
      showFans18: false,
    }),
  );
  const [showAngleReturns, setShowAngleReturns] = useState(() =>
    getStored("ast_showAngleReturns", false),
  );
  const [showLunations, setShowLunations] = useState(() =>
    getStored("ast_showLunations", false),
  );
  const [showSquaringMarkers, setShowSquaringMarkers] = useState(() =>
    getStored("ast_showSquaringMarkers", false),
  );
  const [showApogeePerigee, setShowApogeePerigee] = useState(() =>
    getStored("ast_showApogeePerigee", false),
  );
  const [showAspectReturns, setShowAspectReturns] = useState(() =>
    getStored("ast_showAspectReturns", false),
  );
  const [showComplexAspects, setShowComplexAspects] = useState(() =>
    getStored("ast_showComplexAspects", false),
  );
  const [showHelionTransits, setShowHelionTransits] = useState(() =>
    getStored("ast_showHelionTransits", false),
  );
  const [showLatTransits, setShowLatTransits] = useState(() =>
    getStored("ast_showLatTransits", false),
  );

  // ★ ここに showPyrapoint と showGannFan を統合しました
  const [showPyrapoint, setShowPyrapoint] = useState(() =>
    getStored("ast_showPyrapoint", false),
  );
  const [showGannFan, setShowGannFan] = useState(() =>
    getStored("ast_showGannFan", false),
  );

  const [showPlanetaryAspects, setShowPlanetaryAspects] = useState(() =>
    getStored("ast_showPlanetaryAspects", false),
  );
  const [planetaryAspectConfig, setPlanetaryAspectConfig] = useState(() => {
    const saved = getStored("ast_planetaryAspectCfg", null);
    const defaults = {
      p1: "Sun",
      p2: "Jupiter",
      orb: 2.0,
      aspects: [0, 90, 180],
    };

    // 保存されたデータが期待通りの形式（配列が含まれているか等）かチェックし、不正ならデフォルトを返す
    if (!saved || !Array.isArray(saved.aspects)) return defaults;
    return saved;
  });

  // ============================================================
  // ギャン関連設定 (完全修正版：保存キー同期・重複削除)
  // ============================================================

  const GANN_FAN_DEFAULTS = [
    { label: "1x8", p: 1, t: 8, slope: 0.125, enabled: true, color: "#ff5252" },
    { label: "1x4", p: 1, t: 4, slope: 0.25, enabled: true, color: "#ff7043" },
    { label: "1x3", p: 1, t: 3, slope: 0.333, enabled: true, color: "#ffb74d" },
    { label: "1x2", p: 1, t: 2, slope: 0.5, enabled: true, color: "#ffd54f" },
    { label: "1x1", p: 1, t: 1, slope: 1.0, enabled: true, color: "#4caf50" },
    { label: "2x1", p: 2, t: 1, slope: 2.0, enabled: true, color: "#2196f3" },
    { label: "3x1", p: 3, t: 1, slope: 3.0, enabled: true, color: "#3f51b5" },
    { label: "4x1", p: 4, t: 1, slope: 4.0, enabled: true, color: "#673ab7" },
    { label: "8x1", p: 8, t: 1, slope: 8.0, enabled: true, color: "#9c27b0" },
  ];

  const [gannBasePrice, setGannBasePrice] = useState(() =>
    getStored("ast_gannBasePrice", "126199"),
  );
  const [gannIncrease, setGannIncrease] = useState(() =>
    getStored("ast_gannIncrease", 100),
  );
  const [gannDirection, setGannDirection] = useState(
    () => getStored("ast_gannDirection", "up"), // ★追加: 上昇・下降の方向ステート
  );
  const [gannFanBaseIndex, setGannFanBaseIndex] = useState(null);
  const [gannFanBasePrice, setGannFanBasePrice] = useState(() =>
    getStored("ast_gannFanBasePrice", 0),
  );
  const [gannFanPointsPerUnit, setGannFanPointsPerUnit] = useState(() =>
    getStored("ast_gannFanPPU", 1000),
  );
  const [gannFanDirection, setGannFanDirection] = useState(() =>
    getStored("ast_gannFanDir", "down"),
  );
  const [gannFanCalcMode, setGannFanCalcMode] = useState(() =>
    getStored("ast_gannFanMode", "time"),
  );
  const [gannFanAngleConfigs, setGannFanAngleConfigs] = useState(() =>
    getStored("ast_gannFanConfigs", GANN_FAN_DEFAULTS),
  );

  // ============================================================
  // 3. 各種解析モジュール用ステート (ReferenceError & TypeError 解消)
  // ============================================================

  // 月相設定
  const [moonPhaseConfig, setMoonPhaseConfig] = useState(() =>
    getStored("ast_moonPhaseCfg", {
      showNewMoon: true,
      showFullMoon: true,
      showQuarters: false,
    }),
  );

  // ピラポイント設定
  const [pyrapointConfig, setPyrapointConfig] = useState(() =>
    getStored("ast_pyrapointCfg", { basePrice: 0, mode: "up" }),
  );

  // --- アングルリターン (Angle Return) 関連 ---
  const [angleReturnConfig, setAngleReturnConfig] = useState(() =>
    getStored("ast_angleReturnCfg", {
      coordinate: "longitude",
      selectedAngles: [0, 180],
    }),
  );

  // ★重要：TypeError (reading 'has') を防ぐための Set 定義
  const [selectedAngleReturnPlanets, setSelectedAngleReturnPlanets] = useState(
    () => new Set(getStored("ast_selAngleRetPlanets", [])),
  );

  const [showPlanetaryTrendline, setShowPlanetaryTrendline] = useState(() =>
    getStored("ast_showTrendline", false),
  );
  // ★ 惑星トレンドラインの設定（旧形式からの移行と Set の復元に対応）
  const [trendlineConfig, setTrendlineConfig] = useState(function () {
    var saved = getStored("ast_trendlineCfg", null);
    if (!saved) {
      return { selectedPlanets: new Set(["Saturn"]), multiplier: 100 };
    }
    // 保存データが配列の場合は Set に変換、古い形式（planet単体）の場合は Set で包む
    var planetSet = saved.selectedPlanets
      ? new Set(saved.selectedPlanets)
      : new Set([saved.planet || "Saturn"]);

    return {
      selectedPlanets: planetSet,
      multiplier: saved.multiplier || 100,
    };
  });

  const [selectedRetroPlanets, setSelectedRetroPlanets] = useState(
    () => new Set(getStored("ast_selRetroPlanets", [])),
  );
  const [selectedIngressPlanets, setSelectedIngressPlanets] = useState(
    () => new Set(getStored("ast_selIngressPlanets", [])),
  );
  const [selectedGannPlanets, setSelectedGannPlanets] = useState(
    () => new Set(getStored("ast_selGannPlanets", ["Sun"])),
  );
  const [selectedAspectReturnPlanets, setSelectedAspectReturnPlanets] =
    useState(
      () =>
        new Set(getStored("ast_selAspectRetPlanets", ["Sun", "Moon", "Venus"])),
    );
  const [selectedAspectReturnAngles, setSelectedAspectReturnAngles] = useState(
    () => getStored("ast_selAspectRetAngles", [0, 90, 180, 270]),
  );
  const [selectedHelionPlanets, setSelectedHelionPlanets] = useState(
    () => new Set(getStored("ast_selHelionPlanets", ["Mercury", "Mars"])),
  );
  const [selectedLatPlanets, setSelectedLatPlanets] = useState(
    () => new Set(getStored("ast_selLatPlanets", ["Moon", "Mercury"])),
  );
  const [targetLatValues, setTargetLatValues] = useState(() =>
    getStored("ast_targetLatValues", [0]),
  );
  const [apogeePerigeeCoord, setApogeePerigeeCoord] = useState(() =>
    getStored("ast_apogeeCoord", "ecliptic"),
  );

  // UI操作状態 (★dragModeはここだけに定義)
  const [angleReturnBaseIndex, setAngleReturnBaseIndex] = useState(() =>
    getStored("ast_angleRetBaseIdx", null),
  );
  const [hoverIndex, setHoverIndex] = useState(null);
  const [hoverPrice, setHoverPrice] = useState(null); // ★マウス位置の価格保持用
  const [dragMode, setDragMode] = useState(null);
  const [hoverLunation, setHoverLunation] = useState(null);

  const aspectSettings = useMemo(
    () => props.aspectSettings || DEFAULT_ASPECT_SETTINGS,
    [props.aspectSettings],
  );

  const chartTheme = useMemo(
    function () {
      return {
        bg: themeColors.mainBg,
        grid: themeColors.grid,
        text: isDarkMode ? "#9ca3af" : "#64748b",
        border: themeColors.border,
      };
    },
    [isDarkMode, themeColors],
  );

  // --- 2. 保存用 useEffect (不足していた天体選択の保存を追加) ---
  useEffect(() => {
    const saves = {
      ast_timeframe: timeframe,
      ast_isHeliocentric: isHeliocentric,
      ast_isSidereal: isSidereal,
      ast_visibleCount: visibleCount,
      ast_showGannSquare: showGannSquare,
      ast_showGannSquareLine: showGannSquareLine,
      ast_showGannPlanetaryPrice: showGannPlanetaryPrice,
      ast_gannPlanetaryPriceScale: gannPlanetaryPriceScale,
      ast_gannPlanetaryPriceBase: gannPlanetaryPriceBase,
      ast_gannPlanetaryPriceMirror: gannPlanetaryPriceMirror,
      ast_showAngleReturns: showAngleReturns,
      ast_showLunations: showLunations,
      ast_showApogeePerigee: showApogeePerigee,
      ast_showAspectReturns: showAspectReturns,
      ast_showComplexAspects: showComplexAspects,
      ast_showHelionTransits: showHelionTransits,
      ast_showLatTransits: showLatTransits,
      ast_showGannFan: showGannFan,
      ast_showPyrapoint: showPyrapoint,
      ast_moonPhaseCfg: moonPhaseConfig,
      ast_pyrapointCfg: pyrapointConfig,
      ast_gannBasePrice: gannBasePrice,
      ast_gannIncrease: gannIncrease,
      ast_gannDirection: gannDirection,
      ast_apogeeCoord: apogeePerigeeCoord,
      ast_gannFanPPU: gannFanPointsPerUnit,
      ast_gannFanDir: gannFanDirection,
      ast_gannFanMode: gannFanCalcMode,
      ast_gannFanConfigs: gannFanAngleConfigs,
      ast_gannSquare144Cfg: gannSquare144Config,

      ast_angleReturnCfg: angleReturnConfig,
      // ★追加：アングルリターンの選択天体を保存
      ast_selAngleRetPlanets: Array.from(selectedAngleReturnPlanets),
      ast_showTrendline: showPlanetaryTrendline,
      ast_trendlineCfg: {
        selectedPlanets: Array.from(trendlineConfig.selectedPlanets),
        multiplier: trendlineConfig.multiplier,
      },
      ast_showPlanetaryAspects: showPlanetaryAspects,
      ast_planetaryAspectCfg: planetaryAspectConfig,
      // ★ 以下の天体選択（Set型）を配列に変換して追加
      ast_selRetroPlanets: Array.from(selectedRetroPlanets),
      ast_selIngressPlanets: Array.from(selectedIngressPlanets),
      ast_selGannPlanets: Array.from(selectedGannPlanets),
      ast_selAspectRetPlanets: Array.from(selectedAspectReturnPlanets),
      ast_selHelionPlanets: Array.from(selectedHelionPlanets),
      ast_selLatPlanets: Array.from(selectedLatPlanets),
      ast_angleRetBaseIdx: angleReturnBaseIndex,
      ast_gannFanBasePrice: gannFanBasePrice,
    };

    Object.keys(saves).forEach((key) =>
      localStorage.setItem(
        key,
        typeof saves[key] === "string"
          ? saves[key]
          : JSON.stringify(saves[key]),
      ),
    );
  }, [
    // --- 依存配列の修正：保存をトリガーする変数をすべて網羅 ---
    timeframe,
    isDarkMode,
    isHeliocentric,
    isSidereal,
    visibleCount,
    showGannSquare,
    showGannSquareLine,
    showGannPlanetaryPrice,
    gannPlanetaryPriceScale,
    gannPlanetaryPriceBase,
    gannPlanetaryPriceMirror,
    showAngleReturns,
    showLunations,
    showApogeePerigee,
    showAspectReturns,
    showComplexAspects,
    showHelionTransits,
    showLatTransits,
    showGannFan,
    showPyrapoint,
    moonPhaseConfig,
    pyrapointConfig,
    gannBasePrice,
    gannIncrease,
    gannDirection,
    gannPlanetaryPriceScale,
    gannPlanetaryPriceBase,
    gannPlanetaryPriceMirror,
    apogeePerigeeCoord,
    showPlanetaryTrendline,
    selectedRetroPlanets,
    selectedIngressPlanets,
    selectedGannPlanets,
    selectedAspectReturnPlanets,
    selectedHelionPlanets,
    selectedLatPlanets,
    angleReturnBaseIndex,
    gannFanBasePrice,
    angleReturnConfig,
    selectedAngleReturnPlanets,
    gannFanAngleConfigs, // ★これらを追加
    gannFanCalcMode,
    gannFanPointsPerUnit,
    gannFanDirection,
  ]);

  // --- 1. 日付と価格の完全同期と正規化 ---
  const aggregatedData = useMemo(
    function () {
      if (!csvData || csvData.length === 0) return [];

      // 各行のDate文字列から数値を確実に生成して保存する
      var normalizedCsv = csvData
        .map(function (d) {
          var ts = d.timestamp;
          if (!ts && d.Date) {
            ts = new Date(d.Date).getTime();
          }
          return Object.assign({}, d, { timestamp: ts });
        })
        .filter(function (d) {
          return d.timestamp && !isNaN(d.timestamp);
        });

      if (timeframe === "D") {
        return normalizedCsv.map(function (d) {
          return Object.assign({}, d, {
            dailyPoints: [d],
            Date: new Date(d.timestamp).toISOString().split("T")[0],
          });
        });
      }

      const res = [];
      let bucket = null;
      normalizedCsv.forEach(function (d) {
        const date = new Date(d.timestamp);
        let key =
          timeframe === "W"
            ? date.getFullYear() +
            "-W" +
            Math.ceil(
              ((date - new Date(date.getFullYear(), 0, 1)) / 86400000 + 1) /
              7,
            )
            : date.getFullYear() + "-" + (date.getMonth() + 1);

        if (!bucket || bucket.key !== key) {
          if (bucket) res.push(bucket.data);
          bucket = {
            key: key,
            data: Object.assign({}, d, {
              dailyPoints: [d],
              Date: new Date(d.timestamp).toISOString().split("T")[0],
            }),
          };
        } else {
          bucket.data.High = Math.max(bucket.data.High, d.High);
          bucket.data.Low = Math.min(bucket.data.Low, d.Low);
          bucket.data.Price = d.Price;
          bucket.data.dailyPoints.push(d);
        }
      });
      if (bucket) res.push(bucket.data);
      return res;
    },
    [csvData, timeframe],
  );

  // --- 2. タイムライン生成と未来予測期間の結合 ---
  const fullTimeline = useMemo(
    function () {
      const timeline = aggregatedData.map(function (d) {
        return Object.assign({}, d, { isFuture: false });
      });
      if (timeline.length === 0) return [];

      const last = timeline[timeline.length - 1];
      let futureBars = timeframe === "M" ? 12 : timeframe === "W" ? 52 : 365;
      let stepDays = timeframe === "M" ? 30 : timeframe === "W" ? 7 : 1;

      for (let i = 1; i <= futureBars; i++) {
        const ts = last.timestamp + i * stepDays * 86400000;
        timeline.push({
          timestamp: ts,
          Date: new Date(ts).toISOString().split("T")[0],
          isFuture: true,
          dailyPoints: [],
          Open: null,
          High: null,
          Low: null,
          Price: null,
        });
      }
      return timeline;
    },
    [aggregatedData, timeframe],
  );

  useEffect(
    function () {
      if (fullTimeline.length > 0 && !hasJumped.current) {
        const lastActualIdx = aggregatedData.length - 1;
        const margin = Math.floor(visibleCount * 0.2);
        setStartIndex(Math.max(0, lastActualIdx - (visibleCount - margin)));
        hasJumped.current = true;
      }
    },
    [fullTimeline.length, visibleCount, aggregatedData.length],
  );

  const effectiveStart = Math.min(
    startIndex,
    Math.max(0, fullTimeline.length - visibleCount),
  );

  const displayData = useMemo(
    function () {
      return fullTimeline.slice(effectiveStart, effectiveStart + visibleCount);
    },
    [fullTimeline, effectiveStart, visibleCount],
  );

  // ★ 修正: statsの計算結果を分解して個別のプリミティブ値を保持
  const stats = useMemo(
    function () {
      const prices = displayData
        .filter(function (d) {
          return !d.isFuture && d.High;
        })
        .flatMap(function (d) {
          return [d.High, d.Low];
        });
      if (prices.length === 0) return null;
      const min = Math.min.apply(null, prices),
        max = Math.max.apply(null, prices);
      const center = (min + max) / 2 + priceCenterOffset,
        range = Math.max(1, (max - min) * 1.5 * priceRangeMultiplier);
      return { min: center - range / 2, max: center + range / 2, range: range };
    },
    [displayData, priceRangeMultiplier, priceCenterOffset],
  );

  // ★ stats値を個別のプリミティブに分解（useAstroDataの依存配列用）
  const statsMin = stats ? stats.min : 0;
  const statsMax = stats ? stats.max : 0;
  const statsRange = stats ? stats.range : 1;

  // --- 基準価格のマグニチュード自動調整（デシマル・シフティング） ---
  const normalizedGannParams = useMemo(
    function () {
      var base = parseFloat(gannBasePrice) || 0;
      return { base: base };
    },
    [gannBasePrice, statsMax],
  );

  // ★ useAstroDataに渡すパラメータを最適化して安定化 ★
  const astroDataParams = useMemo(
    function () {
      return {
        fullTimeline: fullTimeline,
        startIndex: effectiveStart,
        visibleCount: visibleCount,
        timeframe: timeframe,
        // useAstroData.js が期待する stats オブジェクトを渡す
        stats: stats,
        isHeliocentric: isHeliocentric,
        isSidereal: isSidereal,
        observerLat: observerLat,
        observerLon: observerLon,
        selectedRetroPlanets: selectedRetroPlanets,
        selectedIngressPlanets: selectedIngressPlanets,
        selectedGannPlanets: selectedGannPlanets,
        // 正規化された数値型を渡す
        gannBasePrice: normalizedGannParams.base,
        showLunations: showLunations,
        aggregatedData: aggregatedData,
        showSquaringMarkers: showSquaringMarkers,
        aspectSettings: aspectSettings,
      };
    },
    [
      fullTimeline,
      effectiveStart,
      visibleCount,
      timeframe,
      stats,
      isHeliocentric,
      isSidereal,
      observerLat,
      observerLon,
      selectedRetroPlanets,
      selectedIngressPlanets,
      selectedGannPlanets,
      normalizedGannParams,
      showLunations,
      aggregatedData,
      showSquaringMarkers,
      aspectSettings,
    ],
  );
  const astroData = useAstroData(astroDataParams);

  // ★ handleChartMouseDownをuseCallbackで安定化
  const handleChartMouseDown = useCallback(
    function (index) {
      if (index !== null && index !== undefined) {
        setAngleReturnBaseIndex(index);
        const data = fullTimeline[index];
        if (data) {
          const targetPrice =
            gannFanDirection === "up"
              ? data.Low || data.Price
              : data.High || data.Price;

          setGannFanBasePrice(targetPrice);

          // ★ 修正：Gann Square of Nine の基準価格も同期させる
          setGannBasePrice(targetPrice.toString());

          // ★ 修正：Pyrapoint の基準点も同期させる
          setPyrapointConfig(function (prev) {
            return Object.assign({}, prev, {
              startIndex: index,
              startPrice: targetPrice,
            });
          });
        }
      }
    },
    [fullTimeline, gannFanDirection],
  );

  // --- 修正：計算引数の順番を PlanetAngleReturnModule.jsx の定義に合わせ、依存配列に天体選択を追加 ---
  const angleReturnHits = useMemo(
    function () {
      if (
        !showAngleReturns ||
        angleReturnBaseIndex === null ||
        !fullTimeline[angleReturnBaseIndex]
      )
        return [];

      const baseRow = fullTimeline[angleReturnBaseIndex];
      const baseDateObj = baseRow.timestamp
        ? new Date(baseRow.timestamp)
        : new Date(baseRow.Date);

      // config オブジェクトに選択中の天体リストを確実に含める
      const calcConfig = Object.assign({}, angleReturnConfig, {
        selectedPlanets: selectedAngleReturnPlanets,
      });

      // PlanetAngleReturnModule.jsx の定義に合わせて 7 つの引数を正しく渡す
      return calculateAngleReturns(
        fullTimeline,
        baseDateObj,
        calcConfig,
        isHeliocentric,
        isSidereal,
        observerLat,
        observerLon,
      );
    },
    [
      fullTimeline,
      angleReturnBaseIndex,
      angleReturnConfig,
      selectedAngleReturnPlanets, // ★重要：天体のON/OFFを即座に計算に反映させるために追加
      isHeliocentric,
      isSidereal,
      showAngleReturns,
      observerLat,
      observerLon,
    ],
  );

  const apogeePerigeeHits = useMemo(
    function () {
      if (!showApogeePerigee) return [];
      return calculateApogeePerigee(fullTimeline);
    },
    [fullTimeline, showApogeePerigee],
  );

  const helionTransitHits = useMemo(
    function () {
      if (!showHelionTransits) return [];
      return calculateHelionTransits(fullTimeline, selectedHelionPlanets);
    },
    [fullTimeline, showHelionTransits, selectedHelionPlanets],
  );

  // ★ ルネーション（月相・蝕）拡張版の計算
  const moonEvents = useMemo(
    function () {
      if (!showLunations) return [];
      return calculateMoonEvents(fullTimeline);
    },
    [fullTimeline, showLunations],
  );

  // ★ 緯度通過（Latitude Transits）の計算
  const latitudeTransitHits = useMemo(
    function () {
      if (!showLatTransits) return [];
      return calculateLatitudeTransits(
        fullTimeline,
        selectedLatPlanets,
        targetLatValues,
        isHeliocentric,
      );
    },
    [
      fullTimeline,
      showLatTransits,
      selectedLatPlanets,
      targetLatValues,
      isHeliocentric,
    ],
  );

  // ★ 複合アスペクト（Complex Aspects）の計算：ReferenceError解消
  const complexAspectHits = useMemo(
    function () {
      if (!showComplexAspects) return [];
      return calculateComplexAspects(
        fullTimeline,
        isHeliocentric,
        aspectSettings,
      );
    },
    [fullTimeline, showComplexAspects, isHeliocentric, aspectSettings],
  );

  const planetaryAspectHits = useMemo(
    function () {
      if (!showPlanetaryAspects) return [];
      return calculatePlanetaryAspects(
        fullTimeline,
        planetaryAspectConfig,
        isHeliocentric,
        isSidereal,
      );
    },
    [
      fullTimeline,
      showPlanetaryAspects,
      planetaryAspectConfig,
      isHeliocentric,
      isSidereal,
    ],
  );

  // ★ アスペクト・リターンの計算：isSidereal を追加して計算と表示を同期
  const aspectReturnHits = useMemo(
    function () {
      if (
        !showAspectReturns ||
        angleReturnBaseIndex === null ||
        !fullTimeline[angleReturnBaseIndex]
      )
        return [];

      const baseRow = fullTimeline[angleReturnBaseIndex];
      const baseDateObj = baseRow.timestamp
        ? new Date(baseRow.timestamp)
        : new Date(baseRow.Date);

      return calculateAspectReturns(
        fullTimeline,
        baseDateObj,
        selectedAspectReturnPlanets,
        selectedAspectReturnAngles,
        isHeliocentric,
        isSidereal, // ★追加：サイデリアル設定を渡す
      );
    },
    [
      fullTimeline,
      angleReturnBaseIndex,
      selectedAspectReturnPlanets,
      selectedAspectReturnAngles,
      isHeliocentric,
      isSidereal, // ★追加：方式切り替え時に再計算
      showAspectReturns,
    ],
  );

  // 座標計算
  const iW = actualWidth - pad.l - pad.r;
  const iH = actualHeight - pad.t - pad.b;

  const getX = useCallback(
    function (i) {
      return pad.l + (i / (visibleCount - 1 || 1)) * iW;
    },
    [pad.l, visibleCount, iW],
  );

  const getGlobalX = useCallback(
    function (globalIdx) {
      return getX(globalIdx - effectiveStart);
    },
    [getX, effectiveStart],
  );

  const getY = useCallback(
    function (p) {
      return pad.t + iH - ((p - statsMin) / statsRange) * iH;
    },
    [pad.t, iH, statsMin, statsRange],
  );

  const candleW = Math.max(1, (iW / visibleCount) * 0.8);

  const priceTicks = useMemo(
    function () {
      if (!stats) return [];
      const rawStep = stats.range / 10,
        mag = Math.pow(10, Math.floor(Math.log10(rawStep || 1)));
      const nice =
        [1, 2, 5, 10].find(function (n) {
          return n * mag >= rawStep;
        }) * mag;
      const ticks = [];
      for (
        let v = Math.ceil(stats.min / nice) * nice;
        v <= stats.max;
        v += nice
      )
        ticks.push(v);
      return ticks;
    },
    [stats],
  );

  // ★ 月初グリッドライン用データ
  const monthStartLines = useMemo(
    function () {
      var lines = [];
      for (var i = 0; i < displayData.length; i++) {
        var d = displayData[i];
        if (!d.timestamp) continue;
        var date = new Date(d.timestamp);
        var prevDate =
          i > 0 && displayData[i - 1].timestamp
            ? new Date(displayData[i - 1].timestamp)
            : null;

        var isTrigger = false;
        if (timeframe === "M" || timeframe === "W") {
          // 月足・週足：1月（年の初め）のバーにのみグリッドを描画
          isTrigger =
            date.getMonth() === 0 &&
            (!prevDate || prevDate.getFullYear() !== date.getFullYear());
        } else {
          // 日足：各月の初めに描画
          isTrigger =
            date.getDate() === 1 ||
            (prevDate && date.getMonth() !== prevDate.getMonth());
        }

        if (isTrigger) {
          lines.push(i);
        }
      }
      return lines;
    },
    [displayData, timeframe], // timeframeを依存配列に追加
  );

  // ★修正：ドラッグ操作中のリスナー再登録を防ぐため、最新の値をRefに保持する
  const dragStateRef = useRef({
    stats,
    actualWidth,
    actualHeight,
    visibleCount,
    fullTimelineLen: fullTimeline.length,
  });

  useEffect(() => {
    dragStateRef.current = {
      stats,
      actualWidth,
      actualHeight,
      visibleCount,
      fullTimelineLen: fullTimeline.length,
    };
  }, [stats, actualWidth, actualHeight, visibleCount, fullTimeline.length]);

  useEffect(
    function () {
      const move = function (e) {
        if (!dragMode) return;

        // Refから最新の値を取得（依存配列による再レンダリングを回避）
        const {
          stats,
          actualWidth,
          actualHeight,
          visibleCount,
          fullTimelineLen,
        } = dragStateRef.current;

        if (dragMode === "pan") {
          const dx = Math.floor(
            (e.clientX - dragStart.current.x) / (actualWidth / visibleCount),
          );
          setStartIndex(
            Math.max(
              0,
              Math.min(
                dragStart.current.startIndex - dx,
                fullTimelineLen - visibleCount,
              ),
            ),
          );
          if (stats)
            setPriceCenterOffset(
              dragStart.current.priceCenterOffset +
              (e.clientY - dragStart.current.y) *
              (stats.range / actualHeight),
            );
        } else if (dragMode === "zoomX") {
          setVisibleCount(
            Math.max(
              30,
              Math.min(
                dragStart.current.count -
                Math.floor((e.clientX - dragStart.current.x) / 5),
                2000,
              ),
            ),
          );
        } else if (dragMode === "zoomY") {
          setPriceRangeMultiplier(
            Math.max(
              0.05,
              dragStart.current.multiplier *
              (1 + (e.clientY - dragStart.current.y) * 0.005),
            ),
          );
        }
      };
      const up = function () {
        setDragMode(null);
      };
      if (dragMode) {
        window.addEventListener("mousemove", move);
        window.addEventListener("mouseup", up);
      }
      return function () {
        window.removeEventListener("mousemove", move);
        window.removeEventListener("mouseup", up);
      };
    },
    [dragMode], // ★依存配列をdragModeのみにし、イベントリスナーの永続性を確保
  );

  const dateLabels = useMemo(
    function () {
      const labels = [];
      let lastX = -300;
      for (let i = 0; i < displayData.length; i++) {
        const d = displayData[i];
        if (!d.timestamp) continue;
        const date = new Date(d.timestamp);
        const prevDate =
          i > 0 && displayData[i - 1].timestamp
            ? new Date(displayData[i - 1].timestamp)
            : null;

        let isTrigger = false;
        let labelText = "";

        if (timeframe === "M" || timeframe === "W") {
          // 月足・週足：年の初めに「西暦」のみを表示
          if (
            date.getMonth() === 0 &&
            (!prevDate || prevDate.getFullYear() !== date.getFullYear())
          ) {
            isTrigger = true;
            labelText = date.getFullYear().toString();
          }
        } else {
          // 日足：各月の初めに「YYYY-MM」を表示
          if (
            date.getDate() <= 7 &&
            (!prevDate || prevDate.getMonth() !== date.getMonth())
          ) {
            isTrigger = true;
            labelText =
              date.getFullYear() +
              "-" +
              (date.getMonth() + 1).toString().padStart(2, "0");
          }
        }

        if (isTrigger) {
          const x = getX(i);
          // ラベルが重ならないためのガード (最小間隔を100pxに拡大して視認性を向上)
          if (x - lastX > 100) {
            labels.push({ x: x, text: labelText, key: d.timestamp });
            lastX = x;
          }
        }
      }
      return labels;
    },
    [displayData, getX, timeframe], // timeframeを依存配列に追加
  );

  useEffect(function () {
    if (!containerRef.current) return;
    const obs = new ResizeObserver(function (entries) {
      const r = entries[0].contentRect;
      if (r.width > 0) setActualWidth(r.width);
      if (r.height > 100) setActualHeight(r.height);
    });
    obs.observe(containerRef.current);
    return function () {
      obs.disconnect();
    };
  }, []);

  // --- 修正：マウスホイールによるズーム機能（横軸拡大・縮小）とページスクロール無効化 ---
  useEffect(
    function () {
      const container = containerRef.current;
      if (!container) return;

      const handleWheel = function (e) {
        // チャート上にマウスがある時は、ブラウザ標準のページスクロールを無効化する
        e.preventDefault();

        // e.deltaY < 0 は上スクロール（拡大：表示本数を減らす）
        // e.deltaY > 0 は下スクロール（縮小：表示本数を増やす）
        const isZoomIn = e.deltaY < 0;
        const zoomFactor = 0.15; // 1回につき 15% の割合でズーム

        setVisibleCount(function (prevCount) {
          const step = Math.ceil(prevCount * zoomFactor);
          let nextCount = isZoomIn ? prevCount - step : prevCount + step;

          // チャートの表示制限 (最小30本〜最大2000本) を維持
          nextCount = Math.max(30, Math.min(nextCount, 2000));

          // 表示本数が変わる場合、現在の表示範囲の「中心」を維持するように startIndex を調整する
          if (nextCount !== prevCount) {
            const diff = nextCount - prevCount;
            setStartIndex(function (prevStart) {
              // 表示本数の増減の半分だけ開始位置をオフセットさせる
              let nextStart = prevStart - Math.floor(diff / 2);
              // タイムラインの最小・最大範囲内に収める
              const maxStart = Math.max(0, fullTimeline.length - nextCount);
              return Math.max(0, Math.min(nextStart, maxStart));
            });
          }
          return nextCount;
        });
      };

      // Reactの onWheel プロパティは passive とみなされることが多いため、
      // 明示的に { passive: false } でイベントリスナーを登録し preventDefault() を有効にする
      container.addEventListener("wheel", handleWheel, { passive: false });

      return function () {
        container.removeEventListener("wheel", handleWheel);
      };
    },
    [fullTimeline.length],
  );

  // ★ settlement: マウスホバー処理を最適化（スロットリング付き）

  // ★★★ 修正: マウスホバー処理を最適化（スロットリング付き）★★★
  const lastHoverUpdateRef = useRef(0);
  const handleContainerMouseMove = useCallback(
    function (e) {
      // スロットリング: 16ms（約60fps）以内の更新をスキップ
      const now = Date.now();
      if (now - lastHoverUpdateRef.current < 16) return;
      lastHoverUpdateRef.current = now;

      if (!containerRef.current) return;
      var rect = containerRef.current.getBoundingClientRect();
      var mouseX = e.clientX - rect.left;
      var mouseY = e.clientY - rect.top;

      // チャート描画領域内かチェック
      if (
        mouseX >= pad.l &&
        mouseX <= actualWidth - pad.r &&
        mouseY >= pad.t &&
        mouseY <= actualHeight - pad.b
      ) {
        var chartX = mouseX - pad.l;
        var chartWidth = actualWidth - pad.l - pad.r;

        // 1. インデックスの更新
        if (visibleCount > 1 && chartWidth > 0) {
          var idx = Math.round((chartX / chartWidth) * (visibleCount - 1));
          if (idx >= 0 && idx < displayData.length) {
            setHoverIndex(function (prev) {
              return prev === idx ? prev : idx;
            });
          }
        }

        // 2. マウス位置に応じた価格の計算
        if (stats && iH > 0) {
          const p = statsMin + (statsRange * (pad.t + iH - mouseY)) / iH;
          setHoverPrice(p);
        }
        return;
      }
      setHoverIndex(null);
      setHoverPrice(null);
    },
    [
      pad.l,
      pad.t,
      pad.r,
      pad.b,
      iH,
      actualWidth,
      actualHeight,
      visibleCount,
      displayData.length,
      stats,
      statsMin,
      statsRange,
    ],
  );

  const handleContainerMouseLeave = useCallback(function () {
    setHoverIndex(null);
    setHoverPrice(null);
  }, []);

  // ★ ホバー中のデータ
  var hoverData = null;
  if (
    hoverIndex !== null &&
    hoverIndex >= 0 &&
    hoverIndex < displayData.length
  ) {
    var d = displayData[hoverIndex];
    if (d && !d.isFuture && d.Date) {
      hoverData = d;
    }
  }

  /**
   * 修正箇所：不足している機能関数 (downloadChart, toggleFS) の追加
   */
  // チャート保存機能

  /**
   * チャート保存機能 (PNG形式・情報合成版)
   * 画面上の不要な重なりを避けつつ、出力画像にのみ銘柄名を合成します。
   */
  const downloadChart = useCallback(
    function () {
      if (!chartRef.current) return;

      try {
        const svgOriginal = chartRef.current;
        const rect = svgOriginal.getBoundingClientRect();
        const width = rect.width;
        const height = rect.height;
        const scale = 2; // 高解像度出力

        // 1. SVGのクローンを作成
        const svgClone = svgOriginal.cloneNode(true);
        svgClone.setAttribute("width", width);
        svgClone.setAttribute("height", height);
        svgClone.setAttribute("viewBox", `0 0 ${width} ${height}`);

        // --- 保存画像にのみ銘柄名を合成する処理 ---
        const nameText = document.createElementNS(
          "http://www.w3.org/2000/svg",
          "text",
        );
        nameText.setAttribute("x", pad.l);
        nameText.setAttribute("y", pad.t - 45);
        nameText.setAttribute("font-size", "12");

        nameText.setAttribute("fill", isDarkMode ? "#ffffff" : "#1e293b");
        nameText.setAttribute("font-family", "sans-serif");
        nameText.textContent = props.chartName;
        svgClone.appendChild(nameText);
        // ----------------------------------------

        // 2. シリアライズしてURL化
        const svgData = new XMLSerializer().serializeToString(svgClone);
        const svgBlob = new Blob([svgData], {
          type: "image/svg+xml;charset=utf-8",
        });
        const url = URL.createObjectURL(svgBlob);

        // 3. Canvasの準備
        const canvas = document.createElement("canvas");
        canvas.width = width * scale;
        canvas.height = height * scale;
        const ctx = canvas.getContext("2d");

        const img = new Image();
        img.onload = function () {
          // 背景の塗りつぶし (透過防止)
          ctx.fillStyle = isDarkMode ? "#0b0e14" : "#ffffff";
          ctx.fillRect(0, 0, canvas.width, canvas.height);

          // 高解像度スケーリングを適用して描画
          ctx.save();
          ctx.scale(scale, scale);
          ctx.drawImage(img, 0, 0, width, height);
          ctx.restore();

          // PNGとして書き出し
          const pngUrl = canvas.toDataURL("image/png");
          const link = document.createElement("a");
          link.href = pngUrl;
          // ファイル名に銘柄名と日付を付与
          const dateStr = new Date().toISOString().split("T")[0];
          link.download = `astronica-${props.chartName.replace(/[/\\?%*:|"<>]/g, "-")}-${dateStr}.png`;
          document.body.appendChild(link);
          link.click();

          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        };
        img.src = url;
      } catch (e) {
        console.error("PNG Download Error:", e);
        alert("画像の保存に失敗しました。");
      }
    },
    [isDarkMode, actualHeight, props.chartName, dateLabels, pad.l, pad.t],
  );

  // 全画面切替機能
  const toggleFS = useCallback(function () {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch(function (err) {
        alert("Error attempting to enable full-screen mode: " + err.message);
      });
    } else {
      document.exitFullscreen();
    }
  }, []);

  const trendlinePoints = useMemo(
    function () {
      return calculatePlanetaryTrendlines(
        fullTimeline,
        angleReturnBaseIndex,
        gannFanBasePrice,
        trendlineConfig,
        isHeliocentric,
      );
    },
    [
      fullTimeline,
      angleReturnBaseIndex,
      gannFanBasePrice,
      trendlineConfig,
      isHeliocentric,
    ],
  );

  // --- Gann Fan 用の座標・範囲オブジェクトをここでメモ化 (return の前に追加) ---
  const gannCoords = useMemo(
    function () {
      return { getX: getX, getY: getY, pad: pad, iH: iH };
    },
    [getX, getY, pad, iH],
  );

  const gannDisplayRange = useMemo(
    function () {
      return { start: effectiveStart, count: visibleCount };
    },
    [effectiveStart, visibleCount],
  );

  // --- 4. JSXレンダリング ---
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        // 透過を一切使わず、ソリッドな背景色を指定
        background: themeColors.mainBg,
        color: themeColors.text,
        borderRadius: "15px",
        overflow: "hidden",
        border: "1px solid " + themeColors.border,
        minHeight: "850px",
      }}
    >
      <div
        ref={containerRef}
        style={{
          position: "relative",
          height: "600px",
          minHeight: "350px",
          resize: "vertical",
          overflow: "hidden",
          background: themeColors.mainBg, // チャートエリア背景
          borderBottom: "1px solid " + themeColors.border,
        }}
        onMouseMove={handleContainerMouseMove}
        onMouseLeave={handleContainerMouseLeave}
      >
        {/* 左上ツールバー：切替アイコンと銘柄名表示 */}
        <div
          style={{
            position: "absolute",
            top: "15px",
            left: "15px",
            zIndex: 110,
            display: "flex",
            flexDirection: "column",
            gap: "10px",
          }}
        >
          <div style={{ display: "flex", gap: "8px" }}>
            {/* タイムフレーム切替 */}
            <div
              style={{
                display: "flex",
                background: isDarkMode ? "#1e222d" : "#f1f5f9",
                borderRadius: "6px",
                padding: "2px",
              }}
            >
              {["D", "W", "M"].map(function (tf) {
                var active = timeframe === tf;
                return (
                  <button
                    key={tf}
                    onClick={function () {
                      setTimeframe(tf);
                    }}
                    style={{
                      padding: "4px 10px",
                      fontSize: "11px",
                      fontWeight: "bold",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      background: active
                        ? isDarkMode
                          ? "#374151"
                          : "#fff"
                        : "transparent",
                      color: active
                        ? isDarkMode
                          ? "#fff"
                          : "#1e293b"
                        : "#64748b",
                      boxShadow: active ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                    }}
                  >
                    {tf === "D" ? "日足" : tf === "W" ? "週足" : "月足"}
                  </button>
                );
              })}
            </div>

            {/* ジオ/ヘリオ切替 */}
            <button
              onClick={function () {
                setIsHeliocentric(!isHeliocentric);
              }}
              style={iconBtnSt(isDarkMode, chartTheme.border)}
              title={isHeliocentric ? "ヘリオセントリック" : "ジオセントリック"}
            >
              <Compass
                size={14}
                style={{ color: isHeliocentric ? "#6366f1" : "inherit" }}
              />
              <span style={{ fontSize: "11px", fontWeight: "bold" }}>
                {isHeliocentric ? "ヘリオセントリック" : "ジオセントリック"}
              </span>
            </button>

            {/* トロピカル/サイデリアル切替 */}
            <button
              onClick={function () {
                setIsSidereal(!isSidereal);
              }}
              style={iconBtnSt(isDarkMode, chartTheme.border)}
              title={isSidereal ? "サイデリアル方式" : "トロピカル方式"}
            >
              <RefreshCw
                size={14}
                style={{ color: isSidereal ? "#10b981" : "inherit" }}
              />
              <span style={{ fontSize: "11px", fontWeight: "bold" }}>
                {isSidereal ? "サイデリアル" : "トロピカル"}
              </span>
            </button>
          </div>

          {/* 現在表示中の銘柄名 (UI用) */}
          <div
            style={{
              fontSize: "12px",

              color: isDarkMode ? "#fff" : "#1e293b",
              textShadow: isDarkMode ? "0 0 10px rgba(0,0,0,0.5)" : "none",
              letterSpacing: "0.05em",
              paddingLeft: "4px",
            }}
          >
            {props.chartName}
          </div>
        </div>

        {/* OHLCデータ表示 */}
        {hoverData && (
          <div
            style={{
              position: "absolute",
              top: "0px",
              left: "15px",
              zIndex: 200,
              display: "flex",
              gap: "15px",
              fontSize: "12px",
              fontFamily:
                '"Helvetica Neue", Arial, "Hiragino Kaku Gothic ProN", "Hiragino Sans", Meiryo, sans-serif',
              backgroundColor: isDarkMode
                ? "rgba(11,14,20,0.95)"
                : "rgba(255,255,255,0.95)",
              padding: "8px 14px",
              pointerEvents: "none",
            }}
          >
            <span style={{ color: isDarkMode ? "#9ca3af" : "#6b7280" }}>
              DATE:{" "}
              <span style={{ color: isDarkMode ? "#e5e7eb" : "#1f2937" }}>
                {hoverData.Date}
              </span>
            </span>
            <span style={{ color: isDarkMode ? "#9ca3af" : "#6b7280" }}>
              始値:{" "}
              <span style={{ color: isDarkMode ? "#e5e7eb" : "#1f2937" }}>
                {hoverData.Open != null ? hoverData.Open.toLocaleString() : "-"}
              </span>
            </span>
            <span>
              <span style={{ color: isDarkMode ? "#22c55e" : "#16a34a" }}>
                高値:{" "}
              </span>
              <span style={{ color: isDarkMode ? "#22c55e" : "#16a34a" }}>
                {hoverData.High != null ? hoverData.High.toLocaleString() : "-"}
              </span>
            </span>
            <span>
              <span style={{ color: isDarkMode ? "#ef4444" : "#dc2626" }}>
                安値:{" "}
              </span>
              <span style={{ color: isDarkMode ? "#ef4444" : "#dc2626" }}>
                {hoverData.Low != null ? hoverData.Low.toLocaleString() : "-"}
              </span>
            </span>
            <span style={{ color: isDarkMode ? "#9ca3af" : "#6b7280" }}>
              終値:
              <span
                style={{
                  color:
                    hoverData.Price != null &&
                      hoverData.Open != null &&
                      hoverData.Price >= hoverData.Open
                      ? isDarkMode
                        ? "#22c55e"
                        : "#16a34a"
                      : isDarkMode
                        ? "#ef4444"
                        : "#dc2626",
                  fontWeight: "600",
                  marginLeft: "4px",
                }}
              >
                {hoverData.Price != null
                  ? hoverData.Price.toLocaleString()
                  : "-"}
              </span>
            </span>
            {hoverData.Price != null && hoverData.Open != null && (
              <span
                style={{
                  color:
                    hoverData.Price >= hoverData.Open
                      ? isDarkMode
                        ? "#22c55e"
                        : "#16a34a"
                      : isDarkMode
                        ? "#ef4444"
                        : "#dc2626",
                  marginLeft: "5px",
                }}
              >
                {(function () {
                  var diff = hoverData.Price - hoverData.Open;
                  var pct = (diff / hoverData.Open) * 100;
                  var sign = diff >= 0 ? "+" : "";
                  return (
                    sign +
                    diff.toLocaleString() +
                    " (" +
                    sign +
                    pct.toFixed(2) +
                    "%)"
                  );
                })()}
              </span>
            )}
          </div>
        )}

        {/* ルネーション ツールチップ */}
        {showLunations && hoverLunation && (
          <div
            style={{
              position: "absolute",
              left: getX(hoverLunation.index - effectiveStart) + 15,
              top: pad.t + iH - 40,
              backgroundColor: isDarkMode
                ? "rgba(30, 41, 59, 0.95)"
                : "rgba(255, 255, 255, 0.95)",
              color: isDarkMode ? "#f1f5f9" : "#1e293b",
              padding: "8px 12px",
              borderRadius: "6px",
              fontSize: "12px",
              border: "1px solid " + chartTheme.border,
              zIndex: 1000,
              pointerEvents: "none",
            }}
          >
            <div style={{ fontWeight: "bold", marginBottom: "2px" }}>
              {hoverLunation.label}
            </div>
            <div>{hoverLunation.date}</div>
          </div>
        )}

        <div
          onMouseDown={function (e) {
            setDragMode("pan");
            dragStart.current = {
              x: e.clientX,
              y: e.clientY,
              startIndex: effectiveStart,
              priceCenterOffset: priceCenterOffset,
              multiplier: priceRangeMultiplier,
              count: visibleCount,
            };
          }}
          onDoubleClick={function (e) {
            const rect = e.currentTarget.getBoundingClientRect();
            const x = e.clientX - rect.left - pad.l;
            const chartAreaWidth = actualWidth - pad.l - pad.r;
            const idx =
              Math.round((x / chartAreaWidth) * (visibleCount - 1)) +
              effectiveStart;
            if (idx >= 0 && idx < fullTimeline.length)
              handleChartMouseDown(idx);
          }}
          style={{ height: "100%", width: "100%", cursor: "crosshair" }}
        >
          <svg
            ref={chartRef}
            width="100%"
            height="100%"
            style={{ display: "block", userSelect: "none" }}
          >
            <g pointerEvents="all">
              {/* 保存画像用：銘柄名描画 (SVG内部) */}

              <RetrogradeLayer
                retrogrades={astroData.retrogrades}
                displayRange={{ start: effectiveStart, count: visibleCount }}
                coords={{ getX: getX, pad: pad, iH: iH, candleW: candleW }}
                planetColors={planetColors}
                isDarkMode={isDarkMode}
              />

              <IngressLayer
                ingresses={astroData.ingresses}
                displayRange={{ start: effectiveStart, count: visibleCount }}
                coords={{ getX: getX, pad: pad, iH: iH }}
                planetColors={planetColors}
                isDarkMode={isDarkMode}
              />

              {monthStartLines.map(function (idx) {
                var xPos = getX(idx);
                return (
                  <line
                    key={"month-grid-" + idx}
                    x1={xPos}
                    y1={pad.t}
                    x2={xPos}
                    y2={pad.t + iH}
                    stroke={
                      isDarkMode ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.1)"
                    }
                    strokeWidth="1"
                  />
                );
              })}

              {priceTicks.map(function (v) {
                return (
                  <g key={"tk-" + v}>
                    <line
                      x1={pad.l}
                      y1={getY(v)}
                      x2={actualWidth - pad.r}
                      y2={getY(v)}
                      stroke={chartTheme.grid}
                    />
                    <text
                      x={actualWidth - pad.r + 5}
                      y={getY(v)}
                      fill={chartTheme.text}
                      fontSize="11"
                      dominantBaseline="middle"
                    >
                      {v.toLocaleString()}
                    </text>
                  </g>
                );
              })}

              <GannSquareLayer
                show={showGannSquare}
                coords={{
                  getX: getGlobalX,
                  getY: getY,
                  pad: pad,
                  actualWidth: actualWidth,
                  iH: iH,
                  effectiveStart: effectiveStart,
                  visibleCount: visibleCount,
                }}
                basePrice={gannBasePrice}
                increase={gannIncrease}
                direction={gannDirection}
                isDark={isDarkMode}
              />

              <GannSquareLineLayer
                show={showGannSquareLine}
                // ダブルクリックで設定された基準点 (AngleReturnと共有)
                baseIndex={angleReturnBaseIndex}
                basePrice={gannFanBasePrice}
                // 設定オブジェクト
                config={gannSquare144Config}
                // 座標変換関数
                coords={{ getX, getY, pad, iH }}
                // ★重要: これが無いとドラッグ時に追従しません
                displayRange={{ start: effectiveStart, count: visibleCount }}
                isDarkMode={isDarkMode}
              />

              <GannPlanetaryPriceLayer
                show={showGannPlanetaryPrice}
                timeline={displayData} // visibleData を displayData に修正
                scale={gannPlanetaryPriceScale}
                base={gannPlanetaryPriceBase}
                mirror={gannPlanetaryPriceMirror}
                selectedPlanets={selectedGannPlanets}
                isHeliocentric={isHeliocentric}
                isSidereal={isSidereal}
                isDarkMode={isDarkMode}
                stats={stats}
                coords={{ getX, getY, pad, actualWidth, iH }}
              />

              {displayData.map(function (d, i) {
                if (d.isFuture || d.High == null || d.Low == null) return null;
                var isUp = d.Price >= d.Open;
                return (
                  <line
                    key={"wick-" + i}
                    x1={getX(i)}
                    y1={getY(d.High)}
                    x2={getX(i)}
                    y2={getY(d.Low)}
                    stroke={isUp ? "#27ae60" : "#e74c3c"}
                    strokeWidth="1"
                  />
                );
              })}

              {displayData.map(function (d, i) {
                if (d.isFuture || !d.Price) return null;
                var isUp = d.Price >= d.Open;
                return (
                  <rect
                    key={"cnd-" + i}
                    x={getX(i) - candleW / 2}
                    y={isUp ? getY(d.Price) : getY(d.Open)}
                    width={candleW}
                    height={Math.max(1, Math.abs(getY(d.Price) - getY(d.Open)))}
                    fill={isUp ? (isDarkMode ? "#0b0e14" : "#fff") : "#e74c3c"}
                    stroke={isUp ? "#27ae60" : "#e74c3c"}
                    strokeWidth="1"
                  />
                );
              })}

              <PyrapointLayer
                show={showPyrapoint}
                displayData={displayData}
                stats={stats}
                config={pyrapointConfig}
                isDarkMode={isDarkMode}
                coords={{ getX, getY, pad, actualWidth, iH }}
              />

              {/* ★背景のカラーバー */}
              <PlanetaryAspectLayer
                show={showPlanetaryAspects}
                hits={planetaryAspectHits}
                coords={{ getX, pad, iH, actualWidth }}
                displayRange={{ start: effectiveStart, count: visibleCount }}
                isDarkMode={isDarkMode}
              />

              {/* ★チャート下部の惑星・アスペクトアイコン (新規追加) */}
              <PlanetaryAspectIconsLayer
                show={showPlanetaryAspects}
                hits={planetaryAspectHits}
                coords={{ getX, pad, iH, actualWidth }}
                displayRange={{ start: effectiveStart, count: visibleCount }}
                isDarkMode={isDarkMode}
                planetColors={planetColors} // 天体色を反映させるために必要
              />
              <MoonPhaseLayer
                show={showLunations}
                lunations={moonEvents}
                config={moonPhaseConfig}
                isDarkMode={isDarkMode}
                setHoverLunation={setHoverLunation}
                coords={{
                  getX: getX,
                  pad: pad,
                  iH: iH,
                  effectiveStart: effectiveStart,
                }}
              />

              {showAngleReturns && (
                <PlanetAngleReturnLayer
                  timeline={fullTimeline}
                  angleReturnHits={angleReturnHits}
                  baseIndex={angleReturnBaseIndex}
                  isDarkMode={isDarkMode} // ★追加：ラベルの保護縁取りを有効化
                  displayRange={{ start: effectiveStart, count: visibleCount }}
                  coords={{ getX: getX, getY: getY, pad: pad, iH: iH }}
                  planetColors={planetColors}
                />
              )}

              <ComplexAspectLayer
                show={showComplexAspects}
                hits={complexAspectHits}
                displayRange={{ start: effectiveStart, count: visibleCount }}
                coords={{ getX: getX, pad: pad, iH: iH }}
                isDarkMode={isDarkMode}
              />

              {showApogeePerigee && (
                <ApogeePerigeeLayer
                  hits={apogeePerigeeHits}
                  displayRange={{ start: effectiveStart, count: visibleCount }}
                  coords={{ getX: getX, pad: pad, iH: iH }}
                  isDarkMode={isDarkMode}
                />
              )}

              {showAspectReturns && (
                <AspectReturnLayer
                  hits={aspectReturnHits}
                  displayRange={{ start: effectiveStart, count: visibleCount }}
                  coords={{ getX: getX, pad: pad, iH: iH }}
                  planetColors={planetColors}
                  isDarkMode={isDarkMode}
                />
              )}

              {showHelionTransits && (
                <HelionTransitLayer
                  hits={helionTransitHits}
                  displayRange={{ start: effectiveStart, count: visibleCount }}
                  coords={{ getX: getX, pad: pad, iH: iH }}
                  planetColors={planetColors}
                  isDarkMode={isDarkMode}
                />
              )}

              {showLatTransits && (
                <LatitudeTransitLayer
                  hits={latitudeTransitHits}
                  displayRange={{ start: effectiveStart, count: visibleCount }}
                  coords={{ getX: getX, pad: pad, iH: iH }}
                  planetColors={planetColors}
                  isDarkMode={isDarkMode}
                />
              )}

              {showGannFan && (
                <GannFanLayer
                  show={showGannFan}
                  baseIndex={angleReturnBaseIndex}
                  basePrice={gannFanBasePrice}
                  pointsPerUnit={gannFanPointsPerUnit}
                  direction={gannFanDirection}
                  angleConfigs={gannFanAngleConfigs}
                  calcMode={gannFanCalcMode}
                  timeline={fullTimeline}
                  isDarkMode={isDarkMode}
                  // 先ほど return の前で定義した変数（オブジェクト）を渡す
                  coords={gannCoords}
                  displayRange={gannDisplayRange}
                />
              )}

              <PlanetaryTrendlineLayer
                show={showPlanetaryTrendline}
                results={trendlinePoints}
                coords={{ getX, getY, pad, iH }}
                isDarkMode={isDarkMode}
                displayRange={{ start: effectiveStart, count: visibleCount }}
              />

              {angleReturnBaseIndex !== null &&
                angleReturnBaseIndex >= effectiveStart &&
                angleReturnBaseIndex < effectiveStart + visibleCount && (
                  <line
                    x1={getX(angleReturnBaseIndex - effectiveStart)}
                    y1={pad.t}
                    x2={getX(angleReturnBaseIndex - effectiveStart)}
                    y2={pad.t + iH}
                    stroke="#ff4d4f"
                    strokeWidth="2"
                    opacity="0.8"
                  />
                )}

              {hoverIndex !== null &&
                hoverIndex >= 0 &&
                hoverIndex < displayData.length && (
                  <g pointerEvents="none">
                    <line
                      x1={getX(hoverIndex)}
                      y1={pad.t}
                      x2={getX(hoverIndex)}
                      y2={pad.t + iH}
                      stroke={
                        isDarkMode ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.4)"
                      }
                      strokeWidth="1"
                      strokeDasharray="4 4"
                    />
                    <foreignObject
                      x={getX(hoverIndex) - 45}
                      y={pad.t + iH + 5}
                      width="90"
                      height="20"
                    >
                      <div
                        style={{
                          backgroundColor: isDarkMode ? "#374151" : "#1e293b",
                          color: "#fff",
                          fontSize: "10px",
                          textAlign: "center",
                          borderRadius: "3px",
                          padding: "2px 0",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                        }}
                      >
                        {displayData[hoverIndex].Date}
                      </div>
                    </foreignObject>

                    {/* --- 追加：水平クロスヘアと価格ラベル (chart.jpg 準拠) --- */}
                    {hoverPrice !== null && (
                      <g>
                        <line
                          x1={pad.l}
                          y1={getY(hoverPrice)}
                          x2={actualWidth - pad.r}
                          y2={getY(hoverPrice)}
                          stroke={
                            isDarkMode
                              ? "rgba(255,255,255,0.5)"
                              : "rgba(0,0,0,0.4)"
                          }
                          strokeWidth="1"
                          strokeDasharray="4 4"
                        />
                        <foreignObject
                          x={actualWidth - pad.r + 2}
                          y={getY(hoverPrice) - 10}
                          width="75"
                          height="20"
                        >
                          <div
                            style={{
                              backgroundColor: "#0000007c", // 画像に近いピンク系
                              color: "#fff",
                              fontSize: "10px",
                              textAlign: "center",
                              borderRadius: "2px",
                              padding: "2px 0",
                              fontWeight: "bold",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                            }}
                          >
                            {hoverPrice.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </div>
                        </foreignObject>
                      </g>
                    )}
                  </g>
                )}
            </g>

            {/* ズーム・パン用エリア */}
            <rect
              x={pad.l}
              y={actualHeight - pad.b}
              width={iW}
              height={pad.b}
              fill="transparent"
              style={{ cursor: "ew-resize" }}
              onMouseDown={function (e) {
                e.stopPropagation();
                setDragMode("zoomX");
                dragStart.current.x = e.clientX;
                dragStart.current.count = visibleCount;
              }}
            />
            <rect
              x={actualWidth - pad.r}
              y={0}
              width={pad.r}
              height={actualHeight}
              fill="transparent"
              style={{ cursor: "ns-resize" }}
              onMouseDown={function (e) {
                e.stopPropagation();
                setDragMode("zoomY");
                dragStart.current.y = e.clientY;
                dragStart.current.multiplier = priceRangeMultiplier;
              }}
            />
          </svg>

          {/* --- 追加：基準点における惑星度数リストの表示 (HTMLオーバーレイ) --- */}
          {showAngleReturns &&
            angleReturnBaseIndex !== null &&
            fullTimeline &&
            fullTimeline[angleReturnBaseIndex] && (
              <BaseDatePlanetList
                basePos={getPlanetPositions(
                  fullTimeline[angleReturnBaseIndex].timestamp
                    ? new Date(fullTimeline[angleReturnBaseIndex].timestamp)
                    : new Date(fullTimeline[angleReturnBaseIndex].Date),
                  isHeliocentric,
                  isSidereal,
                  observerLat,
                  observerLon,
                )}
                coordinate={angleReturnConfig.coordinate}
                planetColors={planetColors}
                isDarkMode={isDarkMode}
              />
            )}

          {/* --- 追加：基準点におけるアスペクト情報の表示 --- */}
          {showAspectReturns && angleReturnBaseIndex !== null && (
            <BaseDateAspectList
              baseDate={
                fullTimeline[angleReturnBaseIndex].timestamp
                  ? new Date(fullTimeline[angleReturnBaseIndex].timestamp)
                  : new Date(fullTimeline[angleReturnBaseIndex].Date)
              }
              selectedPlanets={selectedAspectReturnPlanets}
              selectedAngles={selectedAspectReturnAngles}
              isHelio={isHeliocentric}
              isSidereal={isSidereal} // ★追加
              planetColors={planetColors}
              isDarkMode={isDarkMode}
            />
          )}
        </div>

        {/* 右上ツールバー：切り替えボタンを削除した状態 */}
        <div
          style={{
            position: "absolute",
            top: "15px",
            right: "15px",
            zIndex: 100,
            display: "flex",
            gap: "8px",
          }}
        >
          <button
            onClick={downloadChart}
            style={iconBtnSt(isDarkMode, chartTheme.border)}
            title="PNGで保存"
          >
            <Camera size={16} />
          </button>
          <button
            onClick={toggleFS}
            style={iconBtnSt(isDarkMode, chartTheme.border)}
            title="全画面"
          >
            <Maximize size={16} />
          </button>
        </div>

        {/* UI用日付ラベル（HTMLレイヤー） */}
        {/* UI用日付ラベル（HTMLレイヤー）：一本化してスタイルを最適化 */}
        <div
          style={{
            position: "absolute",
            bottom: "10px", // 少し下に配置
            left: pad.l,
            width: iW,
            height: "20px",
            pointerEvents: "none",
            overflow: "hidden",
          }}
        >
          {dateLabels.map(function (l) {
            return (
              <div
                key={l.key}
                style={{
                  position: "absolute",
                  left: ((l.x - pad.l) / iW) * 100 + "%",
                  transform: "translateX(-50%)",
                  fontSize: "10px",
                  color: isDarkMode ? "#9ca3af" : "#64748b",
                  whiteSpace: "nowrap",
                  textAlign: "center",
                  fontWeight: "500",
                }}
              >
                {l.text}
              </div>
            );
          })}
        </div>
      </div>

      {/* 設定パネル：背景の白を削除し、境界線を薄いグレーに */}
      <div
        style={{
          padding: "24px",
          background: themeColors.mainBg,
          color: themeColors.text,
          borderTop: "1px solid " + themeColors.border,
          display: "flex",
          flexDirection: "column",
          gap: "24px",
          overflowY: "auto",
        }}
      >
        <GannSquareSettingsUI
          show={showGannSquare}
          setShow={setShowGannSquare}
          basePrice={gannBasePrice}
          setBasePrice={setGannBasePrice}
          increase={gannIncrease}
          setIncrease={setGannIncrease}
          direction={gannDirection} // ★追加
          setDirection={setGannDirection} // ★追加
          isDarkMode={isDarkMode}
        />
        <GannPlanetaryPriceSettingsUI
          show={showGannPlanetaryPrice}
          setShow={setShowGannPlanetaryPrice}
          selectedPlanets={selectedGannPlanets}
          setSelectedPlanets={setSelectedGannPlanets}
          scale={gannPlanetaryPriceScale}
          setScale={setGannPlanetaryPriceScale}
          base={gannPlanetaryPriceBase}
          setBase={setGannPlanetaryPriceBase}
          mirror={gannPlanetaryPriceMirror}
          setMirror={setGannPlanetaryPriceMirror}
          isDarkMode={isDarkMode}
        />

        {/* ギャン・ファン設定UI (内部ステート名と完全に一致させた版) */}
        <GannFanSettingsUI
          show={showGannFan}
          setShow={setShowGannFan}
          basePrice={gannFanBasePrice}
          setBasePrice={setGannFanBasePrice}
          pointsPerUnit={gannFanPointsPerUnit}
          setPointsPerUnit={setGannFanPointsPerUnit} // 正しい関数名に修正
          direction={gannFanDirection}
          setDirection={setGannFanDirection}
          angleConfigs={gannFanAngleConfigs}
          setAngleConfigs={setGannFanAngleConfigs}
          calcMode={gannFanCalcMode}
          setCalcMode={setGannFanCalcMode}
          isDarkMode={isDarkMode}
        />
        <GannSquareLineSettingsUI
          show={showGannSquareLine}
          setShow={setShowGannSquareLine}
          config={gannSquare144Config}
          setConfig={setGannSquare144Config}
          isDarkMode={isDarkMode}
        />

        <PyrapointSettingsUI
          show={showPyrapoint}
          setShow={setShowPyrapoint}
          config={pyrapointConfig}
          setConfig={setPyrapointConfig}
          isDarkMode={isDarkMode}
        />
        {/* ★新規追加：Planetary Aspect Settings */}
        <PlanetaryAspectSettingsUI
          show={showPlanetaryAspects}
          setShow={setShowPlanetaryAspects}
          config={planetaryAspectConfig}
          setConfig={setPlanetaryAspectConfig}
          isDarkMode={isDarkMode}
          isHeliocentric={isHeliocentric}
          planetColors={planetColors}
        />

        <AngleReturnSettingsUI
          config={angleReturnConfig}
          setConfig={setAngleReturnConfig}
          showAngleReturns={showAngleReturns}
          setShowAngleReturns={setShowAngleReturns}
          // ★追加：Set オブジェクトを渡すことで内部の .has() エラーを修正
          selectedPlanets={selectedAngleReturnPlanets}
          setSelectedPlanets={setSelectedAngleReturnPlanets}
          isDarkMode={isDarkMode}
          isHeliocentric={isHeliocentric} // ★追加：モードに応じたラベル表示用
        />
        <PlanetaryTrendlineSettingsUI
          show={showPlanetaryTrendline}
          setShow={setShowPlanetaryTrendline}
          config={trendlineConfig}
          setConfig={setTrendlineConfig}
          isDarkMode={isDarkMode}
        />
        <ComplexAspectSettingsUI
          show={showComplexAspects}
          setShow={setShowComplexAspects}
          isDarkMode={isDarkMode}
        />
        <AspectReturnsSettingsUI
          show={showAspectReturns}
          setShow={setShowAspectReturns}
          selectedPlanets={selectedAspectReturnPlanets}
          setSelectedPlanets={setSelectedAspectReturnPlanets}
          selectedAngles={selectedAspectReturnAngles}
          setSelectedAngles={setSelectedAspectReturnAngles}
          isDarkMode={isDarkMode}
        />
        <MoonPhaseSettingsUI
          show={showLunations}
          setShow={setShowLunations}
          config={moonPhaseConfig}
          setConfig={setMoonPhaseConfig}
          isDarkMode={isDarkMode}
        />

        <RetrogradeSettingsUI
          selectedPlanets={selectedRetroPlanets}
          setSelectedPlanets={setSelectedRetroPlanets}
          isDarkMode={isDarkMode}
        />
        <IngressSettingsUI
          selectedPlanets={selectedIngressPlanets}
          setSelectedPlanets={setSelectedIngressPlanets}
          isDarkMode={isDarkMode}
        />
        <ApogeePerigeeSettingsUI
          show={showApogeePerigee}
          setShow={setShowApogeePerigee}
          coordType={apogeePerigeeCoord}
          setCoordType={setApogeePerigeeCoord}
          isDarkMode={isDarkMode}
        />
        <HelionTransitSettingsUI
          show={showHelionTransits}
          setShow={setShowHelionTransits}
          selectedPlanets={selectedHelionPlanets}
          setSelectedPlanets={setSelectedHelionPlanets}
          isDarkMode={isDarkMode}
        />
        <LatitudeTransitSettingsUI
          show={showLatTransits}
          setShow={setShowLatTransits}
          selectedPlanets={selectedLatPlanets}
          setSelectedPlanets={setSelectedLatPlanets}
          targetLatitudes={targetLatValues}
          setTargetLatitudes={setTargetLatValues}
          isDarkMode={isDarkMode}
        />
      </div>
    </div>
  );
}

const btnSt = function (active, dark, border) {
  return {
    padding: "6px 20px",
    fontSize: "11px",
    fontWeight: "bold",
    borderRadius: "4px",
    cursor: "pointer",
    background: active ? "#2c2d96" : dark ? "#1e222d" : "#ffffff",
    color: active ? "#fff" : "#64748b",
    border: "1px solid " + (active ? "#2c2d96" : "#cbd5e1"),
    transition: "all 0.2s",
  };
};
const iconBtnSt = function (dark, border) {
  return {
    padding: "6px 12px",
    fontSize: "11px",
    border: "1px solid " + border,
    borderRadius: "6px",
    background: dark ? "#1e222d" : "#ffffff",
    color: dark ? "#ccc" : "#666",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "5px",
  };
};

const checkDark = function (d) {
  // 明示的に true/dark なら true
  if (d === true || d === "true" || d === "dark") return true;
  // 明示的に false/light なら false
  if (d === false || d === "false" || d === "light") return false;

  // 未定義の場合は DOM から判定
  if (typeof document !== "undefined") {
    return (
      document.documentElement.classList.contains("dark") ||
      document.body.classList.contains("dark") ||
      document.documentElement.getAttribute("data-theme") === "dark"
    );
  }
  return false;
};

const getPnlBoxSt = function (dark) {
  const isDark = checkDark(dark);
  return {
    background: isDark ? "#1c1f26" : "#f8fafc", // 透過を排除
    padding: "20px",
    borderRadius: "12px",
    border: "1px solid " + (isDark ? "#2d313d" : "#e2e8f0"),
    color: isDark ? "#e5e7eb" : "#1e293b",
  };
};

const getPTitleSt = (dark) => {
  const isDark = checkDark(dark);
  return {
    fontSize: "15px",
    fontWeight: "bold",
    color: isDark ? "#ffffff" : "#2c2d96",
    marginBottom: "12px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  };
};

const getLblSt = function (dark) {
  // 修正：d === true ではなく checkDark(dark) を使用
  const isDark = checkDark(dark);
  return {
    fontSize: "11px",
    color: isDark ? "#9ca3af" : "#64748b",
  };
};

const getInpSt = function (dark) {
  // 修正：d === true ではなく checkDark(dark) を使用
  const isDark = checkDark(dark);
  return {
    padding: "8px 12px",
    background: isDark ? "#0b0e14" : "#ffffff",
    color: isDark ? "#ffffff" : "#333333",
    border: "1px solid " + (isDark ? "#374151" : "#cbd5e1"),
    borderRadius: "6px",
    width: "150px",
    outline: "none",
    colorScheme: isDark ? "dark" : "light",
  };
};

const scBtnSt = function (active, dark) {
  const isDark = checkDark(dark);
  return {
    padding: "4px 10px",
    fontSize: "11px",
    border:
      "1px solid " + (active ? "#2c2d96" : isDark ? "#374151" : "#cbd5e1"),
    borderRadius: "4px",
    background: active ? "#2c2d96" : isDark ? "#1e222d" : "#fff",
    color: active ? "#fff" : isDark ? "#9ca3af" : "#64748b",
    cursor: "pointer",
  };
};

const plBtnSt = function (p, active, dark) {
  const isDark = checkDark(dark);
  const planetColor = PLANET_COLORS[p] || "#6366f1";
  return {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "6px 14px",
    borderRadius: "8px",
    fontSize: "12px",
    fontWeight: "600",
    cursor: "pointer",
    // ダークモード時は背景色を濃いグレーに変更（地雷除去）
    background: active
      ? isDark
        ? "#2c2d96"
        : "#ffffff"
      : isDark
        ? "#1c1f26"
        : "#f1f5f9",
    border:
      "1.5px solid " + (active ? planetColor : isDark ? "#2d313d" : "#e2e8f0"),
    color: active
      ? isDark
        ? "#ffffff"
        : planetColor
      : isDark
        ? "#9ca3af"
        : "#64748b",
  };
};
